import { clusterColor, css } from '../../lib/renderer';
import { vosItemId, weightOf, type VosJsonDocument, type VosJsonItem } from '../../lib/vosjson';

/**
 * Read-only views over a VOSviewer JSON document, for everything around the
 * canvas: the legend, the detail panel, the find box, the link readout.
 *
 * Colours come from the renderer's own palette rather than a second copy, so a
 * legend swatch is the colour the map actually painted.
 */

/** CSS colour for an item: its own RGB when the file carries one, otherwise
 *  the cluster palette. `cluster` in the file is 1-based; `clusterColor` takes
 *  a 0-based index. */
export function itemColorOf(item: VosJsonItem): string {
  if (
    typeof item.red === 'number' &&
    typeof item.green === 'number' &&
    typeof item.blue === 'number'
  ) {
    return `rgb(${item.red},${item.green},${item.blue})`;
  }
  return clusterColorOf(typeof item.cluster === 'number' ? item.cluster : 0);
}

export function clusterColorOf(cluster: number): string {
  return css(clusterColor(Math.round(cluster) - 1));
}

export interface ClusterInfo {
  /** 1-based, as written in the file. 0 means the item has no cluster. */
  cluster: number;
  color: string;
  count: number;
  /** The heaviest few labels, so the legend says what a cluster is about. */
  examples: string[];
}

export function summariseClusters(doc: VosJsonDocument, weightKey: string | null): ClusterInfo[] {
  const buckets = new Map<number, VosJsonItem[]>();
  for (const item of doc.network.items) {
    const cluster = typeof item.cluster === 'number' ? Math.round(item.cluster) : 0;
    const bucket = buckets.get(cluster);
    if (bucket) bucket.push(item);
    else buckets.set(cluster, [item]);
  }

  return [...buckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([cluster, items]) => ({
      cluster,
      color: clusterColorOf(cluster),
      count: items.length,
      examples: [...items]
        .sort((a, b) => weightOf(b, weightKey) - weightOf(a, weightKey))
        .slice(0, 3)
        .map((item, index) => item.label ?? vosItemId(item, index)),
    }));
}

export interface Neighbour {
  id: string;
  label: string;
  strength: number;
}

/** The strongest links touching one item, for the detail panel. */
export function neighboursOf(
  doc: VosJsonDocument,
  id: string,
  labels: Map<string, string>,
  limit = 8,
): Neighbour[] {
  const found: Neighbour[] = [];
  for (const link of doc.network.links) {
    const source = String(link.source_id);
    const target = String(link.target_id);
    const other = source === id ? target : target === id ? source : null;
    if (other === null) continue;
    found.push({
      id: other,
      label: labels.get(other) ?? other,
      strength: typeof link.strength === 'number' && Number.isFinite(link.strength) ? link.strength : 1,
    });
  }
  found.sort((a, b) => b.strength - a.strength);
  return found.slice(0, limit);
}

export function labelsById(doc: VosJsonDocument): Map<string, string> {
  const labels = new Map<string, string>();
  doc.network.items.forEach((item, index) => {
    const id = vosItemId(item, index);
    labels.set(id, item.label ?? id);
  });
  return labels;
}

export interface SearchHit {
  id: string;
  label: string;
}

/** Case-insensitive substring search over labels, for the aside's find box. */
export function searchItems(doc: VosJsonDocument, term: string, limit = 12): SearchHit[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return [];
  const out: SearchHit[] = [];
  doc.network.items.forEach((item, index) => {
    if (out.length >= limit) return;
    const label = item.label ?? '';
    if (label.toLowerCase().includes(needle)) {
      out.push({ id: vosItemId(item, index), label });
    }
  });
  return out;
}

/**
 * VOSviewer's own cap on how many link lines a map draws.
 *
 * The link-density slider is a fraction of the total link count, and this is
 * where it starts: a map with 40,000 links opens showing its 1000 strongest,
 * exactly as VOSviewer would, rather than as a solid block of ink.
 */
export const DEFAULT_MAX_LINES = 1000;

export function initialLinkDensity(totalLinks: number): number {
  if (totalLinks <= 0) return 1;
  return Math.min(1, DEFAULT_MAX_LINES / totalLinks);
}

/** How many lines a density fraction draws. */
export function linesFor(totalLinks: number, density: number): number {
  return Math.max(0, Math.round(totalLinks * Math.min(1, Math.max(0, density))));
}
